import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, FileText, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { useQuote } from "@/context/QuoteContext";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, openDialog } = useQuote();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? "bg-ink/90 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex h-[72px] items-center justify-between">
          <Link
            to="/"
            data-testid="logo-home-link"
            className="flex items-center gap-2.5 group"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand transition-colors group-hover:bg-brand-hover">
              <span className="font-display text-lg font-extrabold text-white">M</span>
            </div>
            <div className="leading-none">
              <div className="font-display text-lg font-extrabold uppercase tracking-tight text-white">
                MAGIT
              </div>
              <div className="whitespace-nowrap text-[8px] font-semibold uppercase tracking-[0.3em] text-white/40">
                Impianti & Tecnologie
              </div>
            </div>
          </Link>

          <nav className="hidden items-center gap-0.5 md:flex xl:gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`whitespace-nowrap rounded-md px-2 py-2 text-[11px] lg:px-3.5 lg:text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
                  link.highlight
                    ? "text-brand hover:text-brand-hover"
                    : isActive(link.to)
                    ? "text-white"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {count > 0 && (
              <button
                data-testid="navbar-quote-list"
                onClick={openDialog}
                className="relative hidden rounded-md border border-white/15 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:border-white/40 sm:inline-flex items-center gap-2"
              >
                <FileText className="h-4 w-4" /> <span className="md:hidden xl:inline">Preventivo</span>
                <span
                  data-testid="quote-count-badge"
                  className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white"
                >
                  {count}
                </span>
              </button>
            )}
            <Link
              to="/contatti"
              data-testid="navbar-contatti-cta"
              className="hidden rounded-md bg-brand px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-brand-hover sm:inline-flex md:hidden xl:inline-flex items-center gap-2"
            >
              Contatti <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="p-2 text-white md:hidden"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        <nav
          data-testid="mobile-nav-strip"
          className="-mx-6 flex h-11 items-center justify-between gap-0.5 overflow-x-auto border-t border-white/10 bg-ink/90 px-2 backdrop-blur-xl md:hidden"
        >
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              data-testid={`strip-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`shrink-0 rounded-md px-1.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.05em] ${
                link.highlight ? "text-brand" : isActive(link.to) ? "text-white" : "text-white/60"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="border-t border-white/10 bg-ink px-6 py-5 md:hidden"
        >
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`block py-3 text-sm font-semibold uppercase tracking-[0.12em] ${
                link.highlight ? "text-brand" : "text-white/80"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {count > 0 && (
            <button
              data-testid="mobile-quote-list"
              onClick={openDialog}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-white/15 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white"
            >
              <FileText className="h-4 w-4" /> Preventivo ({count})
            </button>
          )}
          <Link
            to="/contatti"
            data-testid="mobile-cta"
            className="mt-3 flex items-center justify-center gap-2 rounded-md bg-brand px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white"
          >
            Richiedi un preventivo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
